<template>
  <div id="protein-viewer-container">
    <div
        ref="proteinViewer"
        class="protein-viewer"
    ></div>

    <div v-if="loadingError" class="error-container">
      <p>Failed to load the protein structure.</p>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import * as $3Dmol from "3dmol";

export default {
  name: "ProteinPTMview",

  props: {
    pdbId: {
      type: String,
      required: true,
    },
    ptmResidue: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      viewer: null,
      rotateInterval: null,
      loadingError: false,
      destroyed: false,
      pdbUrl: "",
    };
  },

  watch: {

    visible: {
      handler(val) {
        if (val) {
          this.destroyed = false;
          this.safeInit();
        } else {
          this.cleanupViewer();
        }
      },
      immediate: true
    },
    pdbId() {
      if (this.visible) this.rebuildViewer();
    },
    ptmResidue() {
      if (this.visible) this.rebuildViewer();
    },
  },

  mounted() {
    this.pdbUrl = `https://alphafold.ebi.ac.uk/files/AF-${this.pdbId}-F1-model_v6.pdb`;
  },

  methods: {



    extractResidueNumber(res) {
      const m = res?.match(/\d+/);
      return m ? parseInt(m[0], 10) : null;
    },

    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },

    safeInit(retry = 0) {
      if (this.viewer || this.destroyed || !this.visible) return;

      this.$nextTick(() => {
        const el = this.$refs.proteinViewer;
        if (!el) return;

        const w = el.offsetWidth;
        const h = el.offsetHeight;


        if ((w === 0 || h === 0) && retry < 20) {
          requestAnimationFrame(() => this.safeInit(retry + 1));
          return;
        }

        this.initViewer();
      });
    },

    initViewer() {
      if (this.destroyed || !this.visible) return;

      const el = this.$refs.proteinViewer;
      el.innerHTML = '';

      try {

        this.viewer = $3Dmol.createViewer($(el), {
          defaultcolors: $3Dmol.rasmolElementColors,
          backgroundColor: "white",
        });

        const wrapMethod = (obj, methodName) => {
          const original = obj[methodName]?.bind(obj);
          if (!original) return;
          obj[methodName] = (...args) => {

            if (this.destroyed || !this.viewer) return;
            try {
              return original(...args);
            } catch (e) {
              console.warn(`3Dmol ${methodName} blocked during cleanup`);
            }
          };
        };


        wrapMethod(this.viewer, 'render');
        wrapMethod(this.viewer, 'resize');
        wrapMethod(this.viewer, 'show');

        if (this.isSafari()) {
          this.viewer.resize = () => {};
        }

        $.get(this.pdbUrl)
            .done((data) => {
              if (this.destroyed || !this.viewer || !this.visible) return;

              this.viewer.addModel(data, "pdb");
              this.viewer.setStyle({}, { cartoon: { color: "spectrum" } });

              const resi = this.extractResidueNumber(this.ptmResidue);
              if (resi !== null) {
                const model = this.viewer.getModel();
                if (model) {
                  const atoms = model.selectedAtoms({ chain: "A", resi });
                  if (atoms.length > 0) {
                    const { x, y, z } = atoms[0];
                    this.viewer.addLabel(this.ptmResidue, {
                      position: { x: x + 8, y: y + 6, z: z + 6 },
                      fontSize: 14, fontColor: "red", backgroundColor: "white",
                      backgroundOpacity: 0.7, inFront: true,
                    });
                    this.viewer.addArrow({
                      start: { x: x + 10, y, z: z + 10 },
                      end: { x, y, z }, radius: 0.4, radiusRatio: 4, color: "#F5F5F5",
                    });
                  }
                }
              }

              this.viewer.zoomTo();
              if (this.viewer.render) this.viewer.render();
              this.startRotation();

              // el.onclick = () => {
              //   window.open(`https://alphafold.ebi.ac.uk/entry/${this.pdbId}`, "_blank");
              // };
            })
            .fail(() => {
              this.loadingError = true;
            });
      } catch (e) {
        console.error("3Dmol initialization failed:", e);
      }
    },

    startRotation() {
      this.safePause();
      this.rotateInterval = setInterval(() => {
        if (this.destroyed || !this.viewer || !this.visible) {
          this.safePause();
          return;
        }
        try {
          this.viewer.rotate(1, "y");
          this.viewer.render();
        } catch (e) {
          this.safePause();
        }
      }, 150);
    },

    safePause() {
      if (this.rotateInterval) {
        clearInterval(this.rotateInterval);
        this.rotateInterval = null;
      }
    },

    cleanupViewer() {
      this.destroyed = true;
      this.safePause();

      if (this.viewer) {
        try {
          this.viewer.render = () => {};
          this.viewer.resize = () => {};
          this.viewer.show = () => {};

          const canvas = this.viewer.getCanvas();
          if (canvas) {
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (gl) {
              gl.getExtension('WEBGL_lose_context')?.loseContext();
            }
            canvas.remove();
          }

          this.viewer.clear();
        } catch (e) {
          console.warn("Error during 3Dmol cleanup:", e);
        }
      }
      if (this.$refs.proteinViewer) {
        this.$refs.proteinViewer.innerHTML = '';
      }
      this.viewer = null;
    },

    rebuildViewer() {
      this.cleanupViewer();
      this.pdbUrl = `https://alphafold.ebi.ac.uk/files/AF-${this.pdbId}-F1-model_v6.pdb`;
      this.loadingError = false;
      this.destroyed = false;
      this.$nextTick(() => this.safeInit());
    },
  },

  beforeDestroy() {
    this.cleanupViewer();
  },
};
</script>

<style scoped>
#protein-viewer-container {
  position: relative;
  width: 95%;
  min-height: 300px;
}
.protein-viewer {
  width: 100%;
  min-height: 300px;
}
.error-container {
  display: flex;
  justify-content: center;
  align-items: center;
  color: red;
}
</style>



